import React, { useEffect } from 'react';
import { Sticky, inDOM, Paper, Divider, Styler } from '@honeycomb/ui-core';
import { currentTripStateIdVar } from '@honeycomb/data';
import { HotelBasket } from './HotelBasket';
import { HotelHeadline } from './HotelHeadline';
import { isValidEstablishmentPageQuery } from '../../../utils';
const DYNAMIC_HEIGHT_BUFFER = 20;
export function HotelSummary({ BasketProps, isSticky = false, stickyContext, hotelDetails, onReviewsDialogTriggerClick, }) {
    const isClient = inDOM();
    const [isStuck, setIsStuck] = React.useState(false);
    const [isValidQuery, setIsValidQuery] = React.useState(true);
    const containerRef = React.useRef(null);
    const tripStateId = currentTripStateIdVar();
    // Event handling refs
    const ticking = React.useRef(false);
    const frameId = React.useRef(null);
    // The height of the container is dynamically adjusted based on available space.
    const setHeight = React.useCallback(() => {
        ticking.current = false;
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const windowHeight = window.innerHeight;
        const containerRect = container.getBoundingClientRect();
        const availableHeight = windowHeight - containerRect.top - DYNAMIC_HEIGHT_BUFFER;
        // Set max height equal to the available height.
        container.style.maxHeight = `${availableHeight}px`;
        // We need to apply a min-height once the scrollHeight exceeds the clientHeight.
        const { scrollHeight, clientHeight } = container;
        if (scrollHeight > clientHeight) {
            container.style.minHeight = `${scrollHeight}px`;
        }
    }, []);
    const handleResize = React.useCallback(() => {
        // Throttle resize updates using requestAnimationFrame
        if (!ticking.current) {
            ticking.current = true;
            frameId.current = requestAnimationFrame(() => {
                setHeight();
            });
        }
    }, [setHeight]);
    // onmount
    React.useEffect(() => {
        if (!isClient || !isSticky) {
            return undefined;
        }
        // Set height initially and then on each resize.
        setHeight();
        window.addEventListener('resize', handleResize);
        const container = containerRef.current;
        if (!container) {
            return undefined;
        }
        // We also set height following changes to the container content.
        const observer = new MutationObserver(() => {
            // Clear any previously set min-height
            container.style.minHeight = '';
            setHeight();
        });
        observer.observe(container, {
            childList: true,
            subtree: true,
        });
        // on unmount
        return () => {
            window.removeEventListener('resize', setHeight);
            if (frameId.current) {
                cancelAnimationFrame(frameId.current);
            }
            observer.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(() => {
        if (!isSticky) {
            return;
        }
        const container = containerRef.current;
        if (!container) {
            return;
        }
        container.style.minHeight = '';
        setHeight();
    }, [isSticky, isStuck, setHeight]);
    useEffect(() => {
        setIsValidQuery(isValidEstablishmentPageQuery(tripStateId));
    }, [tripStateId]);
    return (React.createElement(Sticky, { context: stickyContext || undefined, onStick: () => setIsStuck(true), onUnstick: () => setIsStuck(false), disabled: !isSticky },
        React.createElement(Paper, { ref: containerRef, component: "section", display: "flex", flexDirection: "column", btlr: isStuck ? 0 : undefined, btrr: isStuck ? 0 : undefined },
            React.createElement(Styler, { pt: 4, "data-default-gutters": true },
                React.createElement(HotelHeadline, { hotelDetails: hotelDetails, compact: isStuck && isValidQuery, onReviewsDialogTriggerClick: onReviewsDialogTriggerClick }),
                React.createElement(Divider, { mt: 3 })),
            isValidQuery && React.createElement(HotelBasket, Object.assign({}, BasketProps)))));
}
